<template>
  <div
    class="accruals-estimate-expand-panel"
    data-row-nav-ignore
    data-testid="accruals-estimate-expand-panel"
    @click.stop
  >
    <accruals-estimate-gl-assign
      v-if="isGlAssignmentMode"
      :group="group"
      :lines="glAssignmentLines"
      :debit-gl-by-id="debitGlById"
      :credit-gl-by-id="creditGlById"
      :can-review="canReview"
      :saving="isApproving || Boolean(savingId)"
      testid="accruals-estimate-expand-panel__gl-assign"
      @update:debitGl="onGlDebitUpdate"
      @update:creditGl="onGlCreditUpdate"
      @save="onSaveGlEntries"
      @open-split-view="onOpenSplitView"
    />

    <div
      v-else
      class="accruals-estimate-expand-panel__card"
      data-testid="accruals-estimate-expand-panel__workbench-card"
    >
      <div class="accruals-estimate-expand-panel__header">
        <div class="accruals-estimate-expand-panel__estimated">
          <span class="accruals-estimate-expand-panel__estimated-label">Estimated Accrual:</span>
          <span class="accruals-estimate-expand-panel__estimated-value">
            {{ formatMoney(selectedTotal) }}
          </span>
        </div>
        <div class="accruals-estimate-expand-panel__po-actions">
          <button
            v-if="showRejectSelected"
            type="button"
            class="accruals-estimate-expand-panel__decision-reject"
            :disabled="isApproving"
            data-testid="accruals-estimate-expand-panel__reject-btn"
            @click.stop="openDecisionDialog('reject')"
          >
            Reject
          </button>
          <button
            v-if="showApproveSelected"
            type="button"
            class="accruals-estimate-expand-panel__decision-approve"
            :disabled="isApproving"
            data-testid="accruals-estimate-expand-panel__decision-approve-btn"
            @click.stop="openDecisionDialog('approve')"
          >
            Approve
          </button>
          <base-button
            v-if="showSubmitSelected"
            primary
            small
            class="shadow-off primaryBtn"
            :loading="isApproving && decisionMode === 'submit'"
            :disabled="isApproving"
            data-testid="accruals-estimate-expand-panel__approve-btn"
            @click.stop="openDecisionDialog('submit')"
          >
            Submit estimate
          </base-button>
        </div>
      </div>

      <div
        class="accruals-estimate-expand-panel__wb"
        data-testid="accruals-estimate-expand-panel__workbench"
      >
          <div class="accruals-estimate-expand-panel__wb-head">
            <span
              class="accruals-estimate-expand-panel__wb-check"
              data-testid="accruals-estimate-expand-panel__wb-check-all"
              @click.stop="onToggleSelectAll"
            >
              <v-checkbox
                v-if="!isManager"
                :input-value="allLinesSelected"
                :indeterminate="someLinesSelected"
                :disabled="!tableLines.length"
                hide-details
                dense
                color="caccounting"
                :ripple="false"
                class="ma-0 pa-0 single-line accruals-estimate-expand-panel__checkbox"
                @change="onSelectAllChange"
              />
            </span>
            <span class="accruals-estimate-expand-panel__wb-ref">REFERENCE</span>
            <span class="accruals-estimate-expand-panel__wb-status-head">STATUS</span>
            <span class="accruals-estimate-expand-panel__wb-source-head">SOURCE</span>
            <span class="accruals-estimate-expand-panel__po-num">RULE EST.</span>
            <span class="accruals-estimate-expand-panel__po-num">NOA EST.</span>
            <span class="accruals-estimate-expand-panel__po-num">CUSTOM EST.</span>
            <span class="accruals-estimate-expand-panel__wb-gl-head">DEBIT GL</span>
            <span class="accruals-estimate-expand-panel__wb-gl-head">CREDIT GL</span>
          </div>

          <div class="accruals-estimate-expand-panel__wb-body">
          <template v-for="(line, index) in tableLines">
            <div
              :key="lineKey(line) || `wb-${index}`"
              class="accruals-estimate-expand-panel__wb-block"
            >
              <div
                class="accruals-estimate-expand-panel__wb-row"
                :data-line-key="lineKey(line)"
                :data-testid="`accruals-estimate-expand-panel__wb-row--${index}`"
                @click="onRowSurfaceClick(line, $event)"
              >
                <span
                  class="accruals-estimate-expand-panel__wb-check"
                  :data-testid="`accruals-estimate-expand-panel__wb-check-wrap--${index}`"
                  @click.stop="onToggleLineClick(line)"
                >
                  <v-checkbox
                    v-if="!isManager && !isApprovedLine(line)"
                    :input-value="isLineSelected(line)"
                    hide-details
                    dense
                    color="caccounting"
                    :ripple="false"
                    class="ma-0 pa-0 single-line accruals-estimate-expand-panel__checkbox"
                    :data-testid="`accruals-estimate-expand-panel__wb-check--${index}`"
                    @click.native.stop.prevent
                  />
                </span>
                <span class="accruals-estimate-expand-panel__ref">
                  <span class="accruals-estimate-expand-panel__ref-line">
                    <span
                      class="accruals-estimate-expand-panel__po-tag"
                      :class="{
                        'accruals-estimate-expand-panel__po-tag--vendor': isMutedRefTag(line),
                      }"
                    >
                      {{ refLabel(line) }}
                    </span>
                    <v-tooltip bottom content-class="tooltip-bottom">
                      <template #activator="{ on, attrs }">
                        <button
                          type="button"
                          class="accruals-estimate-expand-panel__icon-btn"
                          aria-label="Open info panel"
                          :data-testid="`accruals-estimate-expand-panel__wb-split--${index}`"
                          v-bind="attrs"
                          v-on="on"
                          @click.stop="onOpenSplitView(line)"
                        >
                          <img
                            :src="assets.ESTIMATES_SPLIT_VIEW"
                            alt=""
                            class="accruals-estimate-expand-panel__icon-btn-img"
                          />
                        </button>
                      </template>
                      <span>Open info panel</span>
                    </v-tooltip>
                  </span>
                </span>
                <span
                  class="accruals-estimate-expand-panel__wb-status"
                  :data-testid="`accruals-estimate-expand-panel__wb-status--${index}`"
                >
                  <span
                    v-if="lineWorkbenchStatus(line)"
                    class="accruals-estimate-expand-panel__status"
                    :class="`accruals-estimate-expand-panel__status--${lineWorkbenchStatus(line)}`"
                    :data-testid="`accruals-estimate-expand-panel__wb-workbench-status--${index}`"
                  >
                    <span
                      v-if="lineWorkbenchStatus(line) !== 'excluded'"
                      class="accruals-estimate-expand-panel__status-dot"
                      aria-hidden="true"
                    />
                    {{ statusLabel(lineWorkbenchStatus(line)) }}
                  </span>
                </span>
                <span class="accruals-estimate-expand-panel__wb-source">
                  <accruals-estimate-source-toggle
                    v-if="showSourceSelector(line)"
                    :options="lineSourceOptions(line)"
                    :value="lineSource(line)"
                    :disabled="sourceSelectorDisabled(line)"
                    :faded="isManager"
                    :data-testid="`accruals-estimate-expand-panel__wb-source--${index}`"
                    @input="setLineSource(line, $event)"
                  />
                  <span v-else class="accruals-estimate-expand-panel__source-text">
                    {{ viaLabel(line) }}
                  </span>
                </span>
                <span
                  class="accruals-estimate-expand-panel__po-num"
                  :class="{ 'accruals-estimate-expand-panel__amount--emphasis': emphasizeAmount(line, 'rule') }"
                >
                  {{ formatMoney(visibleRuleEstimate(line)) }}
                </span>
                <span
                  class="accruals-estimate-expand-panel__po-num"
                  :class="{
                    'accruals-estimate-expand-panel__muted': !isAmountActive(line, 'noa') && !isIrPending(line),
                    'accruals-estimate-expand-panel__po-num--status': isIrPending(line),
                  }"
                >
                  <span
                    v-if="isIrPending(line)"
                    class="accruals-estimate-expand-panel__ir-pending"
                    :data-testid="`accruals-estimate-expand-panel__wb-noa-status--${index}`"
                  >IR pending</span>
                  <template v-else>
                    {{ formatMoney(visibleNoaEstimate(line)) }}
                  </template>
                </span>
                <span class="accruals-estimate-expand-panel__wb-custom">
                  <input
                    v-if="isCustomActive(line) && canEditLine(line)"
                    :value="poCustomAmount(line)"
                    type="text"
                    class="accruals-estimate-expand-panel__amount-input"
                    :class="{ 'accruals-estimate-expand-panel__amount-input--error': lineError(line, 'amount') }"
                    placeholder="0.00"
                    :data-testid="`accruals-estimate-expand-panel__wb-custom--${index}`"
                    @click.stop
                    @input="onCustomAmountInput(line, $event.target.value)"
                  />
                  <span
                    v-else
                    class="accruals-estimate-expand-panel__po-num"
                    :class="{ 'accruals-estimate-expand-panel__amount--emphasis': emphasizeAmount(line, 'custom') }"
                  >
                    {{ isCustomActive(line) ? formatMoney(poCustomAmount(line) || line.preparerAmount) : '—' }}
                  </span>
                </span>
                <span
                  class="accruals-estimate-expand-panel__wb-gl"
                  :data-testid="`accruals-estimate-expand-panel__wb-debit-gl--${index}`"
                >
                  <div
                    v-if="canEditLine(line)"
                    class="accruals-estimate-expand-panel__gl-select"
                  >
                    <button
                      type="button"
                      class="accruals-estimate-expand-panel__gl-trigger"
                      :class="{ 'accruals-estimate-expand-panel__gl-trigger--error': lineError(line, 'debit') }"
                      @click="toggleGlMenu(line, 'debit', $event)"
                    >
                      <span :class="{ 'accruals-estimate-expand-panel__gl-placeholder': !rowDebitGl(line) }">
                        {{ glCodeOnly(rowDebitGl(line)) || 'Select GL account' }}
                      </span>
                      <span class="accruals-estimate-expand-panel__gl-chevron" aria-hidden="true" />
                    </button>
                    <div
                      v-if="isGlMenuOpen(line, 'debit')"
                      class="accruals-estimate-expand-panel__gl-menu"
                      :style="glMenuBox"
                    >
                      <input
                        v-model="glQuery"
                        type="text"
                        class="accruals-estimate-expand-panel__gl-search"
                        placeholder="Search GL code or name…"
                      />
                      <button
                        v-for="option in filteredGlOptions"
                        :key="`debit-${option.label}`"
                        type="button"
                        class="accruals-estimate-expand-panel__gl-option"
                        :class="{ 'accruals-estimate-expand-panel__gl-option--active': rowDebitGl(line) === option.label }"
                        @click="pickGl(line, 'debit', option.label)"
                      >
                        <span class="accruals-estimate-expand-panel__gl-code">{{ option.code }}</span>
                        <span class="accruals-estimate-expand-panel__gl-name">{{ option.name }}</span>
                      </button>
                    </div>
                  </div>
                  <span
                    v-else
                    :class="{ 'accruals-estimate-expand-panel__muted': debitGlLabel(line) === '—' }"
                  >
                    {{ glCodeOnly(debitGlLabel(line)) }}
                  </span>
                </span>
                <span
                  class="accruals-estimate-expand-panel__wb-gl"
                  :data-testid="`accruals-estimate-expand-panel__wb-credit-gl--${index}`"
                >
                  <div
                    v-if="canEditLine(line)"
                    class="accruals-estimate-expand-panel__gl-select accruals-estimate-expand-panel__gl-select--end"
                  >
                    <button
                      type="button"
                      class="accruals-estimate-expand-panel__gl-trigger"
                      :class="{ 'accruals-estimate-expand-panel__gl-trigger--error': lineError(line, 'credit') }"
                      @click="toggleGlMenu(line, 'credit', $event)"
                    >
                      <span :class="{ 'accruals-estimate-expand-panel__gl-placeholder': !rowCreditGl(line) }">
                        {{ glCodeOnly(rowCreditGl(line)) || 'Select GL account' }}
                      </span>
                      <span class="accruals-estimate-expand-panel__gl-chevron" aria-hidden="true" />
                    </button>
                    <div
                      v-if="isGlMenuOpen(line, 'credit')"
                      class="accruals-estimate-expand-panel__gl-menu accruals-estimate-expand-panel__gl-menu--end"
                      :style="glMenuBox"
                    >
                      <input
                        v-model="glQuery"
                        type="text"
                        class="accruals-estimate-expand-panel__gl-search"
                        placeholder="Search GL code or name…"
                      />
                      <button
                        v-for="option in filteredGlOptions"
                        :key="`credit-${option.label}`"
                        type="button"
                        class="accruals-estimate-expand-panel__gl-option"
                        :class="{ 'accruals-estimate-expand-panel__gl-option--active': rowCreditGl(line) === option.label }"
                        @click="pickGl(line, 'credit', option.label)"
                      >
                        <span class="accruals-estimate-expand-panel__gl-code">{{ option.code }}</span>
                        <span class="accruals-estimate-expand-panel__gl-name">{{ option.name }}</span>
                      </button>
                    </div>
                  </div>
                  <span
                    v-else
                    :class="{ 'accruals-estimate-expand-panel__muted': creditGlLabel(line) === '—' }"
                  >
                    {{ glCodeOnly(creditGlLabel(line)) }}
                  </span>
                </span>
              </div>
            </div>
          </template>
          </div>
        </div>
      </div>

    <lb-dialog
      v-model="commentDialogOpen"
      :heading="confirmDialogHeading"
      width="640"
      content-class="accruals-estimate-confirm-dialog"
      :loading="isApproving"
      data-testid="accruals-estimate-expand-panel__comment-dialog"
      @input="commentDialogOpen = $event"
      @change="commentDialogOpen = $event"
    >
      <template #body>
        <div class="accruals-estimate-expand-panel__confirm">
          <p class="accruals-estimate-expand-panel__confirm-intro">
            {{ confirmIntro }}
          </p>
          <div class="accruals-estimate-expand-panel__confirm-list">
            <div
              v-for="(item, index) in confirmItems"
              :key="item.key"
              class="accruals-estimate-expand-panel__confirm-row"
              :data-testid="`accruals-estimate-expand-panel__confirm-row--${index}`"
            >
              <span
                class="accruals-estimate-expand-panel__po-tag"
                :class="{
                  'accruals-estimate-expand-panel__po-tag--vendor': isMutedRefTag(item.row),
                }"
              >
                {{ item.label }}
              </span>
              <span class="accruals-estimate-expand-panel__confirm-vendor">
                {{ confirmVendorName }}
              </span>
              <span class="accruals-estimate-expand-panel__confirm-source">
                {{ item.sourceLabel }}
              </span>
              <span class="accruals-estimate-expand-panel__confirm-amount">
                {{ formatMoney(item.amount) }}
              </span>
            </div>
          </div>
          <template v-if="decisionMode === 'submit' || decisionMode === 'approve'">
            <label
              class="accruals-estimate-expand-panel__confirm-note-label"
              for="accruals-estimate-expand-panel__confirm-note"
            >
              {{ confirmNoteLabel }}
            </label>
            <textarea
              id="accruals-estimate-expand-panel__confirm-note"
              v-model="sharedComment"
              class="accruals-estimate-expand-panel__confirm-note"
              :placeholder="confirmNotePlaceholder"
              data-testid="accruals-estimate-expand-panel__comment-shared"
            />
          </template>
          <template v-else-if="decisionMode === 'reject'">
            <label
              class="accruals-estimate-expand-panel__confirm-note-label"
              for="accruals-estimate-expand-panel__reject-reason"
            >
              Reason (shared with the preparer)
            </label>
            <textarea
              id="accruals-estimate-expand-panel__reject-reason"
              v-model="decisionReason"
              class="accruals-estimate-expand-panel__confirm-note"
              placeholder="Tell the preparer what to fix (min 3 characters)"
              data-testid="accruals-estimate-expand-panel__reject-reason"
            />
            <p
              v-if="decisionReasonError"
              class="accruals-estimate-expand-panel__confirm-error"
              role="alert"
              data-testid="accruals-estimate-expand-panel__reject-reason-error"
            >
              {{ decisionReasonError }}
            </p>
          </template>
        </div>
      </template>
      <template #actions>
        <div class="accruals-estimate-expand-panel__comment-actions">
          <base-button            
            class="secondaryBtn"
            data-testid="accruals-estimate-expand-panel__comment-cancel-btn"
            @click="commentDialogOpen = false"
          >
            Cancel
          </base-button>
          <base-button
            primary
            small
            class="shadow-off primaryBtn"
            :loading="isApproving"
            data-testid="accruals-estimate-expand-panel__comment-submit-btn"
            @click="confirmDecisionDialog"
          >
            {{ confirmActionLabel }}
          </base-button>
        </div>
      </template>
    </lb-dialog>
  </div>
</template>

<script>
import { ACCRUALS_ASSETS } from '../accrualsPageConstants';
import AccrualsEstimateGlAssign from './AccrualsEstimateGlAssign.vue';
import AccrualsEstimateSourceToggle from './AccrualsEstimateSourceToggle.vue';
import {
  ACCRUALS_APPROVE_DIALOG,
  ACCRUALS_ESTIMATE_AMOUNT_OPTION,
  ACCRUALS_ESTIMATE_ERRORS,
  ACCRUALS_ESTIMATE_STATUS,
  ACCRUALS_ESTIMATE_STATUS_CONFIG,
  ACCRUALS_WORKBENCH_ROLES,
  defaultExpandLineSource,
  expandLineActiveAmount,
  expandLineCustomSeedAmount,
  expandLineSavedCustomAmount,
  expandLineSourceOptions,
  expandWorkbenchLines,
  formatEstimateMoney,
  formatWinningRuleLabel,
  isVendorLevelEstimateRow,
  isWorkbenchGlDue,
  lineGlLabel,
  poNoaDisplayState,
  validateCustomAmount,
  validateRejectReason,
  vendorReferenceLabel,
  visibleLineNoaEstimate,
  visibleLineRuleEstimate,
  workbenchColumnStatusKey,
} from '../accrualsEstimatesConstants';

export default {
  name: 'AccrualsEstimateExpandPanel',
  components: {
    AccrualsEstimateGlAssign,
    AccrualsEstimateSourceToggle,
  },
  props: {
    group: {
      type: Object,
      default: () => ({}),
    },
    source: {
      type: String,
      default: ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE,
    },
    canReview: {
      type: Boolean,
      default: false,
    },
    role: {
      type: String,
      default: '',
    },
    canApprove: {
      type: Boolean,
      default: false,
    },
    confirmingId: {
      type: String,
      default: null,
    },
    savingId: {
      type: String,
      default: null,
    },
    rowMenuItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      assets: ACCRUALS_ASSETS,
      customAmount: '',
      debitGlById: {},
      creditGlById: {},
      poCustomAmounts: {},
      lineSources: {},
      selectedLineKeys: {},
      showFieldErrors: false,
      errorLineKeys: {},
      glMenu: { key: '', field: '' },
      glQuery: '',
      glMenuBox: {},
      commentDialogOpen: false,
      confirmItems: [],
      sharedComment: '',
      decisionMode: 'submit',
      decisionReason: '',
      decisionReasonError: '',
    };
  },
  computed: {
    tableLines() {
      return expandWorkbenchLines(this.group);
    },
    glAssignmentLines() {
      return this.tableLines.filter((line) => this.isAddGl(line));
    },
    isGlAssignmentMode() {
      return false;
    },
    glOptions() {
      const stored = this.$store.state.accruals?.glOptions
        || this.$store.state.m_financialclose?.accruals?.glOptions
        || [];
      return stored.map((label) => {
        const text = String(label || '').trim();
        const parts = text.split(/\s+-\s+/);
        return {
          label: text,
          code: parts[0] || text,
          name: parts.slice(1).join(' - '),
        };
      }).filter((option) => option.label);
    },
    filteredGlOptions() {
      const query = String(this.glQuery || '').trim().toLowerCase();
      if (!query) return this.glOptions;
      return this.glOptions.filter((option) => (
        option.label.toLowerCase().includes(query)
        || option.code.toLowerCase().includes(query)
        || option.name.toLowerCase().includes(query)
      ));
    },
    selectableLines() {
      return this.tableLines.filter((line) => this.canSelectLine(line));
    },
    allLinesSelected() {
      const lines = this.tableLines.filter((line) => !this.isApprovedLine(line));
      return lines.length > 0 && lines.every((line) => this.isLineSelected(line));
    },
    someLinesSelected() {
      const lines = this.tableLines.filter((line) => !this.isApprovedLine(line));
      const selectedCount = lines.filter((line) => this.isLineSelected(line)).length;
      return selectedCount > 0 && selectedCount < lines.length;
    },
    selectedTotal() {
      return this.tableLines.reduce((sum, line) => {
        if (this.isApprovedLine(line) || !this.isLineSelected(line)) return sum;
        const amount = Number(this.activeAmount(line));
        return sum + (Number.isFinite(amount) ? amount : 0);
      }, 0);
    },
    submittableSelectedItems() {
      return this.tableLines
        .filter((line) => this.isLineSelected(line) && this.canSubmitLine(line))
        .map((line) => this.selectionItem(line));
    },
    decisionSelectedItems() {
      return this.tableLines
        .filter((line) => this.isLineSelected(line) && this.canDecideLine(line))
        .map((line) => this.selectionItem(line));
    },
    showSubmitSelected() {
      return this.submittableSelectedItems.length > 0;
    },
    showApproveSelected() {
      return this.decisionSelectedItems.length > 0;
    },
    showRejectSelected() {
      return this.decisionSelectedItems.length > 0;
    },
    isApproving() {
      return Boolean(this.confirmingId);
    },
    confirmDialogHeading() {
      const count = this.confirmItems.length;
      if (this.decisionMode === 'approve') return `Approve selected estimates (${count})`;
      if (this.decisionMode === 'reject') return `Reject selected estimates (${count})`;
      return `Confirm selected estimates (${count})`;
    },
    confirmIntro() {
      if (this.decisionMode === 'approve') return ACCRUALS_APPROVE_DIALOG.INTRO;
      if (this.decisionMode === 'reject') {
        return 'You are about to reject the following line items.';
      }
      return 'You are about to confirm estimates for the following line items. All selected estimates will be submitted for approval.';
    },
    confirmNoteLabel() {
      if (this.decisionMode === 'approve') return ACCRUALS_APPROVE_DIALOG.NOTE_LABEL;
      return 'Note to approver (optional, applies to all)';
    },
    confirmNotePlaceholder() {
      if (this.decisionMode === 'approve') return ACCRUALS_APPROVE_DIALOG.NOTE_PLACEHOLDER;
      return 'Add a note for the approver…';
    },
    confirmActionLabel() {
      if (this.decisionMode === 'approve') return ACCRUALS_APPROVE_DIALOG.CONFIRM_LABEL;
      if (this.decisionMode === 'reject') return 'Reject';
      return 'Confirm all';
    },
    isPreparer() {
      return String(this.role || '').toLowerCase() === ACCRUALS_WORKBENCH_ROLES.PREPARER;
    },
    isManager() {
      return String(this.role || '').toLowerCase() === ACCRUALS_WORKBENCH_ROLES.MANAGER;
    },
    confirmVendorName() {
      return this.group?.vendor || '—';
    },
  },
  watch: {
    group: {
      immediate: true,
      handler() {
        this.syncCustomAmount();
      },
    },
    source() {
      this.syncCustomAmount();
    },
  },
  created() {
    this.$store.dispatch('accruals/loadGlOptions');
    this.onDocumentClick = () => {
      this.closeGlMenu();
    };
    this.onDocumentScroll = (event) => {
      if (!this.glMenu.key) return;
      const menu = event?.target;
      if (menu && menu.closest && menu.closest('.accruals-estimate-expand-panel__gl-menu')) return;
      this.closeGlMenu();
    };
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('scroll', this.onDocumentScroll, true);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('scroll', this.onDocumentScroll, true);
    this.customAmount = '';
    this.debitGlById = {};
    this.creditGlById = {};
    this.poCustomAmounts = {};
    this.lineSources = {};
    this.selectedLineKeys = {};
    this.glMenu = { key: '', field: '' };
    this.glQuery = '';
    this.showFieldErrors = false;
    this.errorLineKeys = {};
    this.commentDialogOpen = false;
    this.confirmItems = [];
    this.sharedComment = '';
    this.decisionMode = 'submit';
    this.decisionReason = '';
    this.decisionReasonError = '';
  },
  methods: {
    formatMoney(value) {
      return formatEstimateMoney(value);
    },
    isAddGl(row) {
      return isWorkbenchGlDue(row);
    },
    lineWorkbenchStatus(line) {
      return workbenchColumnStatusKey(line);
    },
    visibleRuleEstimate(line) {
      return visibleLineRuleEstimate(line);
    },
    visibleNoaEstimate(line) {
      return visibleLineNoaEstimate(line);
    },
    isRowSaving(row) {
      const id = this.lineKey(row);
      return Boolean(this.savingId && id && this.savingId === id);
    },
    poState(line) {
      return poNoaDisplayState(line);
    },
    isIrPending(line) {
      return this.poState(line) === 'ir';
    },
    lineKey(line) {
      return String(line?._id || line?.lineKey || line?.poNumber || '');
    },
    lineSourceOptions(line) {
      return expandLineSourceOptions(line).map((option) => ({
        ...option,
        icon: option.sparkle ? ACCRUALS_ASSETS.ESTIMATES_SPARKLE : null,
      }));
    },
    isVendorRef(line) {
      return isVendorLevelEstimateRow(line) && !line?.poNumber;
    },
    isMutedRefTag(line) {
      if (this.isVendorRef(line)) return true;
      return /^(V|G)-/i.test(String(this.refLabel(line) || ''));
    },
    refLabel(line) {
      return vendorReferenceLabel(this.group, line);
    },
    winningRuleLabel(line) {
      const label = formatWinningRuleLabel(line?.raw || {}, line || {});
      const code = String(line?.locationKey || line?.raw?.subsidiary_code || '').trim();
      const name = String(line?.location || '').trim();
      if (!label || !code || !name || code.toLowerCase() === name.toLowerCase()) return label;
      return label.split(code).join(name);
    },
    isRowConfirming(row) {
      const id = this.lineKey(row);
      return Boolean(this.confirmingId && id && this.confirmingId === id);
    },
    lineSource(line) {
      const key = this.lineKey(line);
      if (this.lineSources[key]) return this.lineSources[key];
      return defaultExpandLineSource(line, this.source);
    },
    setLineSource(line, value) {
      if (this.sourceSelectorDisabled(line)) return;
      const option = expandLineSourceOptions(line).find((entry) => entry.id === value);
      if (!option || option.disabled) return;
      const key = this.lineKey(line);
      this.$set(this.lineSources, key, value);
      if (value !== ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) return;
      const saved = expandLineSavedCustomAmount(line);
      if (saved != null) {
        this.$set(this.poCustomAmounts, key, formatEstimateMoney(saved));
        return;
      }
      const existing = this.poCustomAmounts[key];
      if (existing != null && String(existing).trim() !== '') return;
      const fallback = expandLineCustomSeedAmount(line);
      if (fallback == null || fallback === '') return;
      this.$set(this.poCustomAmounts, key, formatEstimateMoney(fallback));
    },
    isLineSelected(line) {
      const key = this.lineKey(line);
      if (!key) return false;
      if (this.selectedLineKeys[key] == null) return true;
      return Boolean(this.selectedLineKeys[key]);
    },
    canSelectLine(line) {
      return this.canSubmitLine(line) || this.canDecideLine(line);
    },
    canSubmitLine(line) {
      return this.canEditLine(line);
    },
    canDecideLine(line) {
      return this.canApprove
        && this.lineWorkbenchStatus(line) === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL;
    },
    canEditLine(line) {
      if (!this.isPreparer || !this.canReview) return false;
      const status = this.lineWorkbenchStatus(line);
      return status === ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION
        || status === ACCRUALS_ESTIMATE_STATUS.REJECTED
        || status === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL;
    },
    showSourceSelector(line) {
      const status = this.lineWorkbenchStatus(line);
      return status === ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION
        || status === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL
        || status === ACCRUALS_ESTIMATE_STATUS.REJECTED;
    },
    sourceSelectorDisabled(line) {
      if (this.isManager) return true;
      return !this.canEditLine(line);
    },
    toggleLineSelected(line, value) {
      const key = this.lineKey(line);
      if (!key) return;
      this.$set(this.selectedLineKeys, key, Boolean(value));
    },
    onSelectAllChange(value) {
      const next = Boolean(value);
      this.tableLines.filter((line) => !this.isApprovedLine(line)).forEach((line) => {
        const key = this.lineKey(line);
        if (!key) return;
        this.$set(this.selectedLineKeys, key, next);
      });
      console.log('[AccrualsEstimateExpandPanel]:', { action: 'toggle-select-all', selected: next });
    },
    onToggleSelectAll() {
      if (this.isManager || !this.tableLines.length) return;
      this.onSelectAllChange(!this.allLinesSelected);
    },
    onToggleLineClick(line) {
      if (this.isManager || this.isApprovedLine(line)) return;
      this.toggleLineSelected(line, !this.isLineSelected(line));
    },
    isCustomActive(line) {
      return this.lineSource(line) === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM;
    },
    isApprovedLine(line) {
      return this.lineWorkbenchStatus(line) === ACCRUALS_ESTIMATE_STATUS.APPROVED;
    },
    isAmountActive(line, kind) {
      return this.lineSource(line) === kind;
    },
    emphasizeAmount(line, kind) {
      return this.isApprovedLine(line) && this.isAmountActive(line, kind);
    },
    activeAmount(line) {
      return expandLineActiveAmount(line, this.lineSource(line), this.poCustomAmount(line));
    },
    selectionItem(line) {
      const note = String(line.preparerNote || line.raw?.preparer_note || '').trim();
      return {
        row: line,
        source: this.lineSource(line),
        amount: this.activeAmount(line),
        debitGl: this.rowDebitGl(line),
        creditGl: this.rowCreditGl(line),
        note: note.length >= 8 ? note : '',
      };
    },
    lineError(line, field) {
      const key = this.lineKey(line);
      if (!this.showFieldErrors || !key || !this.errorLineKeys[key]) return '';
      return this.errorsForLine(line)[field] || '';
    },
    errorsForLine(line) {
      const debit = this.rowDebitGl(line);
      const credit = this.rowCreditGl(line);
      const errors = { amount: '', debit: '', credit: '' };
      if (this.lineSource(line) === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) {
        errors.amount = validateCustomAmount(this.poCustomAmount(line)) || '';
      } else if (!(Number(this.activeAmount(line)) > 0)) {
        errors.amount = ACCRUALS_ESTIMATE_ERRORS.ESTIMATE_REQUIRED;
      }
      if (!debit) errors.debit = 'Select a debit GL.';
      if (!credit) errors.credit = 'Select a credit GL.';
      else if (debit && debit === credit) errors.credit = ACCRUALS_ESTIMATE_ERRORS.GL_MUST_DIFFER;
      return errors;
    },
    isGlMenuOpen(line, field) {
      return this.glMenu.key === this.lineKey(line) && this.glMenu.field === field;
    },
    closeGlMenu() {
      if (!this.glMenu.key && !this.glMenu.field) return;
      this.glMenu = { key: '', field: '' };
      this.glQuery = '';
    },
    toggleGlMenu(line, field, event) {
      if (this.isGlMenuOpen(line, field)) {
        this.closeGlMenu();
        return;
      }
      const rect = event?.currentTarget?.getBoundingClientRect?.() || { bottom: 0, left: 0, right: 0 };
      const alignEnd = field === 'credit';
      this.glQuery = '';
      this.glMenu = { key: this.lineKey(line), field };
      this.glMenuBox = {
        top: `${rect.bottom + 4}px`,
        left: alignEnd ? 'auto' : `${rect.left}px`,
        right: alignEnd ? `${window.innerWidth - rect.right}px` : 'auto',
      };
    },
    pickGl(line, field, label) {
      if (field === 'debit') this.setRowDebitGl(line, label);
      else this.setRowCreditGl(line, label);
      this.closeGlMenu();
    },
    statusLabel(status) {
      return ACCRUALS_ESTIMATE_STATUS_CONFIG[status]?.label || status || '—';
    },
    poCustomAmount(line) {
      const key = this.lineKey(line);
      if (this.poCustomAmounts[key] != null) return this.poCustomAmounts[key];
      return '';
    },
    glCodeOnly(value) {
      const text = String(value || '').trim();
      if (!text || text === '—') return text;
      const match = this.glOptions.find((option) => option.label === text || option.code === text);
      if (match?.code) return match.code;
      return text.split(/\s+-\s+/)[0] || text;
    },
    debitGlLabel(line) {
      return lineGlLabel(line, 'debit');
    },
    creditGlLabel(line) {
      return lineGlLabel(line, 'credit');
    },
    setPoCustomAmount(line, value) {
      this.$set(this.poCustomAmounts, this.lineKey(line), value);
    },
    onCustomAmountInput(line, value) {
      this.setPoCustomAmount(line, value);
      if (String(value || '').trim()) {
        this.setLineSource(line, ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM);
      }
    },
    viaLabel(line) {
      const source = this.lineSource(line);
      if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.NOA) return 'Noa';
      if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) return 'Custom';
      return 'Rule';
    },
    syncCustomAmount() {
      const fallback = this.group?.noaEstimate ?? this.group?.ruleEstimate ?? this.group?.amount;
      this.customAmount = fallback != null && fallback !== '' ? formatEstimateMoney(fallback) : '';
      const debit = { ...this.debitGlById };
      const credit = { ...this.creditGlById };
      const sources = { ...this.lineSources };
      const selected = { ...this.selectedLineKeys };
      const amounts = { ...this.poCustomAmounts };
      this.tableLines.forEach((row) => {
        const key = this.lineKey(row);
        if (!key) return;
        if (debit[key] == null) debit[key] = row.debitGl || '';
        if (credit[key] == null) credit[key] = row.creditGl || '';
        const allowed = expandLineSourceOptions(row)
          .filter((option) => !option.disabled)
          .map((option) => option.id);
        if (!sources[key] || !allowed.includes(sources[key])) {
          sources[key] = defaultExpandLineSource(row, this.source);
        }
        if (selected[key] == null) selected[key] = true;
        const savedCustom = expandLineSavedCustomAmount(row);
        if (savedCustom != null) {
          amounts[key] = formatEstimateMoney(savedCustom);
        } else if (amounts[key] == null && sources[key] === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) {
          const seed = expandLineCustomSeedAmount(row);
          amounts[key] = seed != null && seed !== '' ? formatEstimateMoney(seed) : '';
        }
      });
      this.debitGlById = debit;
      this.creditGlById = credit;
      this.lineSources = sources;
      this.selectedLineKeys = selected;
      this.poCustomAmounts = amounts;
    },
    rowDebitGl(row) {
      const saved = this.debitGlById[this.lineKey(row)];
      if (saved) return saved;
      const label = this.debitGlLabel(row);
      return label === '—' ? '' : label;
    },
    rowCreditGl(row) {
      const saved = this.creditGlById[this.lineKey(row)];
      if (saved) return saved;
      const label = this.creditGlLabel(row);
      return label === '—' ? '' : label;
    },
    setRowDebitGl(row, value) {
      this.$set(this.debitGlById, this.lineKey(row), value);
    },
    setRowCreditGl(row, value) {
      this.$set(this.creditGlById, this.lineKey(row), value);
    },
    onGlDebitUpdate({ line, value }) {
      this.setRowDebitGl(line, value);
    },
    onGlCreditUpdate({ line, value }) {
      this.setRowCreditGl(line, value);
    },
    onSaveGlEntries(payload) {
      const items = payload?.items || [];
      if (!this.canReview || !items.length) return;
      console.log('[AccrualsEstimateExpandPanel]:', {
        action: 'save-gl',
        groupId: this.group?._id,
        count: items.length,
      });
      this.$emit('save-gl', {
        group: this.group,
        row: items[0].row,
        items,
      });
    },
    onRowSurfaceClick(line, event) {
      const target = event?.target;
      if (target && typeof target.closest === 'function') {
        const control = target.closest(
          '.accruals-estimate-expand-panel__wb-check, .accruals-estimate-source-toggle, .accruals-estimate-expand-panel__amount-input, .accruals-estimate-expand-panel__gl-select, .accruals-estimate-expand-panel__gl-menu'
        );
        if (control) return;
      }
      this.onOpenSplitView(line);
    },
    onOpenSplitView(row) {
      this.$emit('open-split-view', row);
    },
    emitConfirm(row, source, amount, note) {
      if (!this.canReview || !row || amount == null) return;
      console.log('[AccrualsEstimateExpandPanel]:', {
        action: 'confirm',
        source,
        amount,
        groupId: this.group?._id,
        rowId: row._id,
        lineKey: row.lineKey,
      });
      this.$emit('confirm', {
        group: this.group,
        row,
        source,
        amount,
        note: note || '',
        items: [{ row, source, amount, note: note || '' }],
      });
    },
    openDecisionDialog(mode) {
      const items = mode === 'submit' ? this.submittableSelectedItems : this.decisionSelectedItems;
      if (!items.length) return;
      if (mode === 'submit' && this.hasInvalidSubmitFields(items)) {
        console.log('[AccrualsEstimateExpandPanel]:', {
          action: 'submit-validation-failed',
          count: items.length,
        });
        return;
      }
      this.decisionMode = mode;
      this.decisionReason = '';
      this.decisionReasonError = '';
      this.confirmItems = items.map((item) => ({
        ...item,
        key: this.lineKey(item.row),
        label: this.refLabel(item.row),
        sourceLabel: this.viaLabel(item.row),
      }));
      this.sharedComment = '';
      this.$nextTick(() => {
        this.commentDialogOpen = true;
      });
      console.log('[AccrualsEstimateExpandPanel]:', {
        action: 'open-confirm-dialog',
        mode,
        count: this.confirmItems.length,
        groupId: this.group?._id,
      });
    },
    hasInvalidSubmitFields(items) {
      const keys = {};
      (items || []).forEach((item) => {
        const key = this.lineKey(item.row);
        if (key) keys[key] = true;
      });
      this.errorLineKeys = keys;
      this.showFieldErrors = true;
      const first = (items || []).find((item) => {
        const errors = this.errorsForLine(item.row);
        return errors.amount || errors.debit || errors.credit;
      });
      if (!first) return false;
      const errors = this.errorsForLine(first.row);
      let message = 'Select a debit GL and a credit GL.';
      if (errors.debit && errors.credit && errors.credit === ACCRUALS_ESTIMATE_ERRORS.GL_MUST_DIFFER) {
        message = errors.credit;
      } else if (!errors.debit && !errors.credit && errors.amount) {
        message = errors.amount;
      }
      this.$store.commit('sbError', message, { root: true });
      this.$nextTick(() => this.scrollToLine(first.row));
      return true;
    },
    scrollToLine(line) {
      const key = this.lineKey(line);
      if (!key || !this.$el) return;
      const row = this.$el.querySelector(`[data-line-key="${key}"]`);
      if (row && typeof row.scrollIntoView === 'function') {
        row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    },
    submitSelectedItems(items) {
      if (!items.length) return;
      this.commentDialogOpen = false;
      console.log('[AccrualsEstimateExpandPanel]:', {
        action: 'submit-selected',
        count: items.length,
        groupId: this.group?._id,
      });
      this.$emit('confirm', {
        group: this.group,
        row: items[0].row,
        source: items[0].source,
        amount: items[0].amount,
        debitGl: items[0].debitGl,
        creditGl: items[0].creditGl,
        note: items[0].note,
        items,
      });
    },
    confirmCommentDialog() {
      const items = this.confirmItems;
      if (!items.length) return;
      if (this.hasInvalidSubmitFields(items)) {
        this.commentDialogOpen = false;
        console.log('[AccrualsEstimateExpandPanel]:', {
          action: 'submit-validation-failed',
          count: items.length,
        });
        return;
      }
      const note = String(this.sharedComment || '').trim();
      this.submitSelectedItems(items.map((item) => ({ ...item, note })));
    },
    confirmDecisionDialog() {
      if (this.decisionMode === 'approve') {
        const rows = this.confirmItems.map((item) => item.row).filter(Boolean);
        const note = String(this.sharedComment || '').trim();
        this.commentDialogOpen = false;
        console.log('[AccrualsEstimateExpandPanel]:', {
          action: 'approve-selected',
          count: rows.length,
          groupId: this.group?._id,
        });
        this.$emit('approve', { group: this.group, rows, note });
        return;
      }
      if (this.decisionMode === 'reject') {
        const error = validateRejectReason(this.decisionReason);
        this.decisionReasonError = error;
        if (error) {
          this.$store.commit('sbError', error, { root: true });
          console.log('[AccrualsEstimateExpandPanel]:', {
            action: 'reject-validation-failed',
            error,
          });
          return;
        }
        const rows = this.confirmItems.map((item) => item.row).filter(Boolean);
        const reason = String(this.decisionReason).trim();
        this.commentDialogOpen = false;
        console.log('[AccrualsEstimateExpandPanel]:', {
          action: 'reject-selected',
          count: rows.length,
          groupId: this.group?._id,
        });
        this.$emit('reject', { group: this.group, rows, reason });
        return;
      }
      this.confirmCommentDialog();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimateExpandPanel.scss';
</style>

<style lang="scss">
.accruals-estimate-confirm-dialog {
  overflow: hidden;
  border-radius: var(--radius-sm) !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);

  .v-card__title.popupHeader {
    min-height: 0;
    padding: var(--space-9) var(--space-lg) !important;
    background-color: var(--grey-white) !important;
    color: var(--color-primary-base) !important;
  }

  .v-card__title.popupHeader h5 {
    color: var(--color-primary-base) !important;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-semibold);
    line-height: 1.45;
  }

  .v-card__title.popupHeader .v-btn {
    display: none;
  }

  .v-card__text {
    padding: var(--space-lg) !important;
    border-top: var(--border-sm) solid var(--grey-100);
  }

  .v-card__actions {
    margin: 0;
    padding: var(--space-md) var(--space-lg) !important;
    border-top: var(--border-sm) solid var(--grey-100);
  }
}
</style>
